/**
 * One `@wtfalch/auth/browser` client per agora, each with its own
 * `sessionStorage` namespace -- the "agora seams" the package documents for an
 * app juggling more than one issuer session.
 *
 * The callback is `/callback`, shared by every agora, because the agora app is
 * one SPA at one origin. What tells the shared callback which agora's flow it
 * is finishing is the pending sign-in record below, not the store: the
 * store is namespaced *to* an Agora, so a caller has to already know the agora to
 * pick the right one, and at the callback that is exactly what is not yet
 * known.
 */
import {
  type BrowserAuth,
  type BrowserAuthStore,
  type BrowserSession,
  createBrowserAuth,
} from '@wtfalch/auth/browser';

import type { Agora } from './agoras.ts';
import { desktopAuth, isDesktop } from './desktop-auth.ts';

export type Session = BrowserSession;
export const CALLBACK_PATH = '/callback';

/** A `BrowserAuthStore` backed by `sessionStorage`, every key prefixed with
 *  the agora's id. Two agoras given different ids never read or write each
 *  other's verifier, token or silent-tried mark -- the prefix is the whole
 *  isolation. Every access is wrapped: a browser that refuses storage must
 *  lose the session on reload, not crash the app. */
export function storeForAgora(agoraId: string): BrowserAuthStore {
  const key = (k: string) => `agora:${agoraId}:${k}`;
  return {
    get(k) {
      try {
        return sessionStorage.getItem(key(k));
      } catch {
        return null;
      }
    },
    set(k, value) {
      try {
        sessionStorage.setItem(key(k), value);
      } catch {
        // Best effort; see module doc.
      }
    },
    drop(k) {
      try {
        sessionStorage.removeItem(key(k));
      } catch {
        // Best effort.
      }
    },
  };
}

/**
 * The default `scope` (`openid email profile offline_access`) is left
 * unset here because it already is what mail needs: `profile` is what lets
 * Stalwart resolve a mailbox from `preferred_username`, and
 * `offline_access` is what makes `refresh()` below have anything to
 * rotate. `extraScopes` adds the resource owner claim mail needs, plus an
 * audience scope for each of the agora's two projects, so the one token this
 * app holds is accepted by both Stalwart and messaging.
 */
export function authForAgora(agora: Agora): BrowserAuth {
  const desktop = desktopAuth(agora);
  if (desktop) return desktop;
  return createBrowserAuth({
    issuer: agora.issuer,
    clientId: agora.clientId,
    callbackPath: CALLBACK_PATH,
    extraScopes: [
      // Without this, the issuer reuses any open session -- another
      // company's, or the platform's -- and mail/chat refuse that user
      // with 401 (agora#431). Scoping the request to this agora's own org
      // means a session of another org gets the login screen instead.
      `urn:zitadel:iam:org:id:${agora.organisationId}`,
      'urn:zitadel:iam:user:resourceowner',
      `urn:zitadel:iam:org:project:id:${agora.mailProjectId}:aud`,
      `urn:zitadel:iam:org:project:id:${agora.messagingProjectId}:aud`,
      // Only when Files is provisioned for this agora (agora#353) -- an
      // agora with no `filesProjectId` has nothing to ask an audience for.
      ...(agora.filesProjectId
        ? [`urn:zitadel:iam:org:project:id:${agora.filesProjectId}:aud`]
        : []),
    ],
    store: storeForAgora(agora.id),
  });
}

const PENDING_SIGNIN_KEY = 'wtfalch-agora:pending-signin';

/**
 * Remember which agora's sign-in is in flight, then leave for the issuer.
 *
 * Kept in its own top-level key rather than one of the per-agora stores above,
 * because at this point in the flow the agora is not yet something `/callback`
 * can look up a store *by* -- it is the thing being looked up. A failure to
 * write it is not fatal: the callback below falls back to the agora list
 * rather than guessing, so worst case is an extra click, not a crash.
 */
export function startSignIn(
  auth: BrowserAuth,
  agoraId: string,
  returnTo?: string,
  loginHint?: string,
): Promise<void> {
  if (isDesktop()) return auth.signIn(returnTo);
  try {
    sessionStorage.setItem(PENDING_SIGNIN_KEY, agoraId);
  } catch {
    // Best effort; see doc above.
  }
  if (!loginHint) return auth.signIn(returnTo);
  return auth.signIn(returnTo, { loginHint });
}

/**
 * The silent half of `startSignIn`: ask the issuer to authorise without
 * interacting, at most once per tab. Somebody with a live session at the
 * issuer comes straight back signed in and never sees a sign-in screen
 * (agora#250). Resolves false when there was nothing to try; otherwise the
 * browser leaves. `/callback` needs the same pending agora id either way.
 */
export function startSilentSignIn(
  auth: BrowserAuth,
  agoraId: string,
  returnTo?: string,
): Promise<boolean> {
  if (!auth.silentSignInAvailable()) return Promise.resolve(false);
  try {
    sessionStorage.setItem(PENDING_SIGNIN_KEY, agoraId);
  } catch {
    // Best effort; see `startSignIn`.
  }
  return auth.trySilentSignIn(returnTo);
}

/**
 * Leave for the issuer's end-session page, so a sign-out also ends the
 * session there. Without this the next tab asks the issuer silently and is
 * let straight back in. The issuer returns to `/`, the one post-logout
 * address foundry registers for an Agora's client.
 */
export function endIssuerSession(agora: Agora): void {
  // A trailing slash on the issuer would otherwise double up here
  // (agora#450), the same pattern already stripped before the SDK's own
  // authorize and token calls.
  const url = new URL(`${agora.issuer.replace(/\/+$/, '')}/oidc/v1/end_session`);
  url.searchParams.set('client_id', agora.clientId);
  url.searchParams.set('post_logout_redirect_uri', `${window.location.origin}/`);
  window.location.assign(url);
}

export function readPendingSignInAgoraId(): string | null {
  try {
    return sessionStorage.getItem(PENDING_SIGNIN_KEY);
  } catch {
    return null;
  }
}

export function clearPendingSignIn(): void {
  try {
    sessionStorage.removeItem(PENDING_SIGNIN_KEY);
  } catch {
    // Best effort.
  }
}

/** A minute of slack, matching `BrowserSession`'s own expiry check -- a token
 *  that dies mid-request is worse than one renewed a minute early. */
const REFRESH_SLACK_MS = 60_000;

export function needsRefresh(session: Pick<Session, 'expiresAt'>): boolean {
  return session.expiresAt - Date.now() < REFRESH_SLACK_MS;
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  return atob(padded + padding);
}

/** The token's own claims, read with no signature check -- shared by
 *  `hasAudience` and `tokenSubject` below. `null` for anything not
 *  JWT-shaped or not valid base64url JSON, the same as either used to
 *  handle its own decode failure before this was split out. */
function decodedClaims(accessToken: string): Record<string, unknown> | null {
  const [, payloadSegment] = accessToken.split('.');
  if (!payloadSegment) return null;
  try {
    return JSON.parse(base64UrlDecode(payloadSegment)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Whether a ZITADEL access token carries the given project's audience --
 * read straight off the token's own `aud` claim, with no signature check.
 * This only decides what the agora *shows*: Chat beside Mail when the
 * messaging project's audience is there, hidden otherwise. Every request
 * the token actually authorises is still verified server-side
 * (`chat-service`'s `TokenVerifier`, mail's own session check), so a token
 * this cannot parse, or one somebody tampered with, carries no audience
 * here and gets refused there. `extraScopes` in `authForAgora` is what asks
 * ZITADEL to put a project's id in `aud` in the first place.
 */
export function hasAudience(accessToken: string, audience: string): boolean {
  const aud = decodedClaims(accessToken)?.aud;
  if (typeof aud === 'string') return aud === audience;
  return Array.isArray(aud) && aud.includes(audience);
}

/**
 * Whether `accessToken` decodes at all -- not a signature check, just shape.
 * This agora's own ZITADEL app mints opaque access tokens on purpose
 * (`authForAgora`'s own doc above, agora#505), so `hasAudience` reading no
 * audience off one is "nothing to read", not "read it and it said no".
 * Callers that can tell the two apart use this to decide whether `aud` is
 * worth asking at all.
 */
export function isJwtShaped(accessToken: string): boolean {
  return decodedClaims(accessToken) !== null;
}

const USERINFO_TIMEOUT_MS = 5_000;
const TOKEN_SUBJECT_CACHE_TTL_MS = 60_000;
const trimSlash = (value: string) => value.replace(/\/+$/, '');

/** A successful answer only, per access token, for a minute -- a refusal is
 *  never cached, so a transient issuer problem never sticks past the
 *  token's own next use (agora#512). */
const tokenSubjectCache = new Map<string, { subject: string; expires: number }>();

/**
 * The person behind this session's access token, asked of the issuer's
 * userinfo endpoint. A ZITADEL userinfo call accepts a bearer token
 * regardless of its shape, which decoding the access token for its own
 * `sub` claim cannot: this agora's tokens are opaque on purpose (agora#505,
 * auth#321), and `realFiles()`'s subject used to come from exactly that
 * decode. Mirrors the same pattern `apps/hr/src/identity.ts` uses
 * server-side. Resolves `null` on any refusal, bad shape or network
 * failure -- the caller already treats "no subject" as "no Files for this
 * session".
 *
 * Trusts the issuer's own discovery document for where the userinfo
 * endpoint is, rather than a hardcoded path, but only once the document
 * both names this issuer as its own and offers an endpoint on the issuer's
 * own origin (agora#417, agora#512): a document served from, or naming, a
 * different origin could otherwise hand this session's bearer token to
 * whoever answers there. Caches a resolved subject for a minute so a
 * caller that asks repeatedly for the same token (every render, every
 * effect dependency change) does not hit the issuer every time.
 */
export async function tokenSubject(
  agora: Pick<Agora, 'issuer'>,
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  const cached = tokenSubjectCache.get(accessToken);
  if (cached && cached.expires > Date.now()) return cached.subject;
  try {
    const issuer = trimSlash(agora.issuer);
    const discovery = await fetchImpl(new URL('/.well-known/openid-configuration', issuer), {
      signal: AbortSignal.timeout(USERINFO_TIMEOUT_MS),
    });
    if (!discovery.ok) return null;
    const found = (await discovery.json()) as { issuer?: string; userinfo_endpoint?: string };
    if (typeof found.issuer !== 'string' || trimSlash(found.issuer) !== issuer) return null;
    if (!found.userinfo_endpoint) return null;
    if (new URL(found.userinfo_endpoint).origin !== new URL(issuer).origin) return null;
    const response = await fetchImpl(found.userinfo_endpoint, {
      headers: { authorization: `Bearer ${accessToken}` },
      signal: AbortSignal.timeout(USERINFO_TIMEOUT_MS),
    });
    if (!response.ok) return null;
    const claims = (await response.json()) as Record<string, unknown>;
    const subject = typeof claims.sub === 'string' && claims.sub.length > 0 ? claims.sub : null;
    if (subject)
      tokenSubjectCache.set(accessToken, {
        subject,
        expires: Date.now() + TOKEN_SUBJECT_CACHE_TTL_MS,
      });
    return subject;
  } catch {
    return null;
  }
}
